import React, { useState, useEffect, useCallback, useRef } from "react";
import { Camera, ZoomIn, ZoomOut, X, Lock } from "lucide-react";
import { useSiteContext } from "../../../../context/SiteContext";
import requests from "../../../../services/httpServices";

const CROP_VIEWPORT = 200;
const CROP_OUTPUT = 320;

const getInitials = (name) => {
  if (!name) return "M";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2);
};

export default function MemberProfile() {
  const { user } = useSiteContext();
  const [profile, setProfile] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [notifications, setNotifications] = useState({});
  const [toast, setToast] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const cropImgRef = useRef(null);

  const memberId = user?.memberId;

  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2000);
  }, []);

  useEffect(() => {
    if (!memberId) return;
    let isMounted = true;
    const loadProfile = async () => {
      setProfileLoading(true);
      setProfileError("");
      try {
        const data = await requests.get(`/member/${memberId}/profile`);
        if (data?.success === false) throw new Error(data?.message || "Failed to load profile.");
        const memberProfile = data?.member || {};
        const firstName = memberProfile.firstName || "";
        const lastName = memberProfile.lastName || "";
        const name = memberProfile.name || `${firstName} ${lastName}`.trim();
        const email = memberProfile.email || user?.email || "";
        const joined = memberProfile.created_at
          ? new Date(memberProfile.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
          : "";
        const resolvedProfile = {
          name, firstName, lastName, email,
          phone: memberProfile.phone || "",
          timezone: memberProfile.timezone || "",
          joined,
          plan: memberProfile.plan || user?.plan || "",
          avatarUrl: memberProfile.avatarUrl || memberProfile.photo || "",
          initials: getInitials(name || email),
          notifications: memberProfile.notifications || {},
        };
        if (!isMounted) return;
        setProfile(resolvedProfile);
        const raw = resolvedProfile.notifications || {};
        setNotifications({
          meetingReminders: raw.meetingReminders ?? raw.meeting_reminders ?? false,
          billingAlerts: raw.billingAlerts ?? raw.billing_alerts ?? true,
          storageWarnings: raw.storageWarnings ?? raw.storage_warnings ?? true,
          productUpdates: raw.productUpdates ?? raw.announcements ?? false,
        });
        setEditValues({ firstName, lastName, email, phone: resolvedProfile.phone, timezone: resolvedProfile.timezone });
      } catch (error) {
        if (isMounted) setProfileError(error?.message || "Failed to load profile.");
      } finally {
        if (isMounted) setProfileLoading(false);
      }
    };
    loadProfile();
    return () => { isMounted = false; };
  }, [memberId, user?.email, user?.plan]);

  const handleSaveClick = async () => {
    if (!memberId) return;
    setIsSaving(true);
    try {
      const data = await requests.patch(`/member/${memberId}/profile`, {
        firstName: editValues.firstName,
        lastName: editValues.lastName,
        phone: editValues.phone,
      });
      if (data?.success === false) throw new Error(data?.message || "Failed to update profile.");
      setProfile((prev) => prev ? ({
        ...prev,
        name: `${editValues.firstName} ${editValues.lastName}`.trim(),
        firstName: editValues.firstName,
        lastName: editValues.lastName,
        phone: editValues.phone,
      }) : prev);
      showToast("Profile saved!");
    } catch (err) {
      showToast(err?.message || "Failed to update profile.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleNotification = async (key) => {
    const updated = { ...notifications, [key]: !notifications[key] };
    setNotifications(updated);
    if (!memberId) return;
    try {
      await requests.patch(`/member/${memberId}/profile`, {
        notifications: {
          meeting_reminders: updated.meetingReminders ?? false,
          billing_alerts: updated.billingAlerts ?? false,
          storage_warnings: updated.storageWarnings ?? false,
          announcements: updated.productUpdates ?? false,
        },
      });
      showToast("Preferences saved!");
    } catch {
      /* silent */
    }
  };

  const handleAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !memberId) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      setCropSrc(src);
      setOffset({ x: 0, y: 0 });
      const img = new Image();
      img.onload = () => {
        const fitZoom = CROP_VIEWPORT / Math.min(img.naturalWidth, img.naturalHeight);
        setZoom(fitZoom);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = useCallback(async () => {
    const img = cropImgRef.current;
    if (!cropSrc || !img || !memberId) return;
    setAvatarUploading(true);
    setCropSrc(null);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = CROP_OUTPUT;
      canvas.height = CROP_OUTPUT;
      const ctx = canvas.getContext("2d");
      ctx.beginPath();
      ctx.arc(CROP_OUTPUT / 2, CROP_OUTPUT / 2, CROP_OUTPUT / 2, 0, Math.PI * 2);
      ctx.clip();
      const outputScale = CROP_OUTPUT / CROP_VIEWPORT;
      const scaledW = img.naturalWidth * zoom * outputScale;
      const scaledH = img.naturalHeight * zoom * outputScale;
      const drawX = (CROP_OUTPUT - scaledW) / 2 + offset.x * outputScale;
      const drawY = (CROP_OUTPUT - scaledH) / 2 + offset.y * outputScale;
      ctx.drawImage(img, drawX, drawY, scaledW, scaledH);
      const blob = await new Promise((res, rej) =>
        canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/png", 1)
      );
      const form = new FormData();
      form.append("avatar", blob, "avatar.png");
      const data = await requests.post(`/member/${memberId}/upload-avatar`, form);
      if (data?.success === false) throw new Error(data?.message || "Upload failed");
      setProfile((prev) => (prev ? { ...prev, avatarUrl: data.avatarUrl } : prev));
      showToast("Photo updated!");
    } catch (err) {
      showToast(err?.message || "Failed to upload photo");
    } finally {
      setAvatarUploading(false);
    }
  }, [cropSrc, zoom, offset, memberId, showToast]);

  const passwordRequirements = [
    { id: "length", label: "Minimum 8 characters (better: 10–12)", test: (v) => v.length >= 8 },
    { id: "upper", label: "At least 1 uppercase letter (A–Z)", test: (v) => /[A-Z]/.test(v) },
    { id: "lower", label: "At least 1 lowercase letter (a–z)", test: (v) => /[a-z]/.test(v) },
    { id: "number", label: "At least 1 number (0–9)", test: (v) => /[0-9]/.test(v) },
    { id: "special", label: "At least 1 special character (@ $ ! % * ? &)", test: (v) => /[@$!%*?&]/.test(v) },
    { id: "nospace", label: "No spaces", test: (v) => !/\s/.test(v) },
  ];

  const handleChangePassword = async () => {
    if (!memberId) return;
    if (!passwordForm.next || !passwordForm.confirm) {
      setPasswordError("Please fill all password fields.");
      return;
    }
    if (passwordRequirements.some((r) => !r.test(passwordForm.next))) {
      setPasswordError("Password does not meet the required rules.");
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }
    setPasswordLoading(true);
    setPasswordError("");
    try {
      const data = await requests.post(`/member/${memberId}/change-password`, {
        new_password: passwordForm.next,
      });
      if (data?.success === false) throw new Error(data?.message || "Failed to update password.");
      showToast("Password updated successfully.");
      setShowPasswordModal(false);
      setPasswordForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPasswordError(err?.message || "Failed to update password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!memberId) {
    return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Member account required.</div>;
  }
  if (profileLoading || !profile) {
    return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Loading profile...</div>;
  }

  return (
    <div style={{ padding: "8px 16px 40px", fontFamily: "'DM Sans', sans-serif", color: "#ffffff" }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>PROFILE</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#ffffff", margin: "0 0 8px 0" }}>My Profile</h1>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", margin: 0 }}>Manage your account details and preferences.</p>
        {profileError && <div style={{ marginTop: 10, fontSize: 12, color: "#ef4444" }}>{profileError}</div>}
      </div>

      <div style={cardStyle(28, 32)}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div style={{ position: "relative", width: 72, height: 72 }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "linear-gradient(135deg, #6a1fbf, #a64dff)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#ffffff", fontSize: 24, fontWeight: 700, overflow: "hidden",
              }}>
                {profile.avatarUrl ? (
                  <img src={profile.avatarUrl} alt={profile.name || "Member avatar"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span>{profile.initials}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
                aria-label={avatarUploading ? "Uploading photo" : "Edit photo"}
                title={avatarUploading ? "Uploading..." : "Edit photo"}
                style={{
                  position: "absolute", bottom: -2, right: -2,
                  width: 26, height: 26, borderRadius: "50%",
                  background: "#6a1fbf", color: "#ffffff",
                  border: "2px solid #060621",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: avatarUploading ? "not-allowed" : "pointer",
                  padding: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  opacity: avatarUploading ? 0.6 : 1,
                  transition: "transform 0.15s ease",
                }}
                onMouseEnter={(e) => !avatarUploading && (e.currentTarget.style.transform = "scale(1.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <Camera size={13} />
              </button>
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#ffffff", marginBottom: 4 }}>{profile.name}</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>{profile.email}</div>
              {profile.joined && <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Member since {profile.joined}</div>}
              {profile.plan && (
                <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: profile.plan === "Business" ? "rgba(106,31,191,0.2)" : "rgba(0,200,83,0.2)",
                  color: profile.plan === "Business" ? "#a64dff" : "#00c853" }}>
                  {profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1)} Plan
                </span>
              )}
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarFileChange} />
          <button
            onClick={() => { setPasswordForm({ current: "", next: "", confirm: "" }); setPasswordError(""); setPasswordTouched(false); setShowPasswordModal(true); }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "10px 16px", borderRadius: 8,
              border: "none",
              background: "linear-gradient(135deg, #6a1fbf, #a64dff)",
              color: "#ffffff",
              cursor: "pointer",
              fontSize: 13, fontWeight: 700,
            }}
          >
            <Lock size={14} /> Change Password
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24, marginBottom: 32 }}>
        <div style={cardStyle(24)}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: "0 0 20px" }}>Personal Information</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {["firstName", "lastName", "email", "phone", "timezone"].map((field) => (
              <div key={field} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>
                  {field === "firstName" ? "First Name" : field === "lastName" ? "Last Name" : field === "email" ? "Email Address" : field === "phone" ? "Phone Number" : "Timezone"}
                </label>
                <input
                  type="text"
                  value={editValues[field] ?? ""}
                  readOnly={field === "email"}
                  onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
                  style={{
                    padding: "10px 14px", borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: field === "email" ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.03)",
                    color: field === "email" ? "rgba(255,255,255,0.4)" : "#ffffff",
                    fontSize: 14, outline: "none",
                    cursor: field === "email" ? "default" : "text",
                  }}
                />
              </div>
            ))}
          </div>
          <button
            onClick={handleSaveClick}
            disabled={isSaving}
            style={{
              marginTop: 20, width: "100%", padding: "11px 0", borderRadius: 8, border: "none",
              background: isSaving ? "rgba(106,31,191,0.5)" : "linear-gradient(135deg, #6a1fbf, #a64dff)",
              color: "#ffffff", fontSize: 14, fontWeight: 700,
              cursor: isSaving ? "not-allowed" : "pointer",
            }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        <div style={cardStyle(24)}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: "0 0 20px" }}>Notification Preferences</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { key: "meetingReminders", label: "Meeting Reminders", desc: "Get notified 15 min before meetings" },
              { key: "billingAlerts", label: "Billing Alerts", desc: "Invoice and payment notifications" },
              { key: "storageWarnings", label: "Storage Warnings", desc: "Alerts when storage is above 80%" },
              { key: "productUpdates", label: "Product Updates", desc: "New features and announcements" },
            ].map((notif) => (
              <div key={notif.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", marginBottom: 4 }}>{notif.label}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>{notif.desc}</div>
                </div>
                <button
                  onClick={() => handleToggleNotification(notif.key)}
                  style={{
                    width: 44, height: 24, borderRadius: 12, border: "none",
                    background: notifications[notif.key] ? "#00c853" : "rgba(255,255,255,0.1)",
                    cursor: "pointer", position: "relative",
                    display: "flex", alignItems: "center", padding: 2, boxSizing: "border-box",
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%", background: "#ffffff",
                    transition: "transform 0.2s ease",
                    transform: notifications[notif.key] ? "translateX(20px)" : "translateX(0px)",
                  }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>


      {toast && (
        <div style={{
          position: "fixed", bottom: 40, right: 40, padding: "16px 28px", borderRadius: 12,
          background: "linear-gradient(135deg, #6a1fbf, #a64dff)", color: "#ffffff",
          fontSize: 16, fontWeight: 700, boxShadow: "0 12px 32px rgba(106,31,191,0.5)",
          zIndex: 9999, minWidth: 200, textAlign: "center",
        }}>
          {toast}
        </div>
      )}

      {showPasswordModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 200, padding: 24,
        }}
          onClick={() => { if (!passwordLoading) setShowPasswordModal(false); }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            width: "100%", maxWidth: 420, background: "#0d1b3e",
            borderRadius: 14, padding: 24, border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginBottom: 8 }}>Change Password</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#ffffff", marginBottom: 16 }}>Update your password</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { label: "New Password", key: "next" },
                { label: "Confirm New Password", key: "confirm" },
              ].map((f) => (
                <div key={f.key}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>{f.label}</label>
                  <input
                    type="password"
                    value={passwordForm[f.key]}
                    onChange={(e) => {
                      if (f.key === "next") setPasswordTouched(true);
                      setPasswordForm((prev) => ({ ...prev, [f.key]: e.target.value }));
                      if (f.key === "next") setPasswordError("");
                    }}
                    style={{
                      marginTop: 6, width: "100%", padding: "10px 12px", borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.04)", color: "#ffffff", outline: "none",
                    }}
                  />
                  {f.key === "next" && (
                    <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.6 }}>
                      {passwordRequirements.map((rule) => {
                        const isValid = rule.test(passwordForm.next);
                        const showState = passwordTouched || passwordForm.next.length > 0;
                        return (
                          <div key={rule.id} style={{
                            color: !showState ? "rgba(255,255,255,0.45)" : isValid ? "#00c853" : "#ef4444",
                            display: "flex", alignItems: "center", gap: 6,
                          }}>
                            <span>{showState ? (isValid ? "✓" : "•") : "•"}</span>
                            <span>{rule.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {passwordError && <div style={{ marginTop: 12, fontSize: 12, color: "#ef4444" }}>{passwordError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowPasswordModal(false)} disabled={passwordLoading}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "rgba(255,255,255,0.7)", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleChangePassword} disabled={passwordLoading}
                style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #6a1fbf, #a64dff)", color: "#ffffff", fontWeight: 600, cursor: "pointer", opacity: passwordLoading ? 0.7 : 1 }}>
                {passwordLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cropSrc && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 3000,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'DM Sans', sans-serif",
          }}
          onMouseMove={(e) => {
            if (!dragging || !dragStart) return;
            setOffset({ x: dragStart.ox + (e.clientX - dragStart.x), y: dragStart.oy + (e.clientY - dragStart.y) });
          }}
          onMouseUp={() => { setDragging(false); setDragStart(null); }}
          onMouseLeave={() => { setDragging(false); setDragStart(null); }}
        >
          <div
            style={{
              background: "#0d1117",
              borderRadius: 16,
              padding: 28,
              width: 320,
              display: "flex", flexDirection: "column", gap: 20,
              boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#ffffff" }}>Crop Profile Photo</span>
              <button
                onClick={() => setCropSrc(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>
            <div
              style={{
                width: CROP_VIEWPORT, height: CROP_VIEWPORT, borderRadius: "50%", overflow: "hidden",
                position: "relative", alignSelf: "center",
                background: "#1a1f2e",
                cursor: dragging ? "grabbing" : "grab",
                border: "2px solid rgba(106,31,191,0.6)",
                userSelect: "none",
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                setDragging(true);
                setDragStart({ x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y });
              }}
              onWheel={(e) => {
                e.preventDefault();
                setZoom((z) => Math.min(z * 3, Math.max(z * 0.2, z + (e.deltaY > 0 ? -z * 0.1 : z * 0.1))));
              }}
            >
              <img
                ref={cropImgRef}
                src={cropSrc}
                alt="crop"
                draggable={false}
                style={{
                  position: "absolute", width: "auto", height: "auto",
                  maxWidth: "none", maxHeight: "none",
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
                  top: "50%", left: "50%", transformOrigin: "center", pointerEvents: "none",
                }}
              />
              <div style={{ position: "absolute", inset: 0, pointerEvents: "none", border: "1px solid rgba(255,255,255,0.15)" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={() => setZoom((z) => Math.max(0.01, z * 0.85))}
                style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#ffffff", display: "flex", alignItems: "center" }}
              >
                <ZoomOut size={16} />
              </button>
              <input
                type="range" min={0.01} max={2} step={0.01} value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: "#6a1fbf" }}
              />
              <button
                onClick={() => setZoom((z) => Math.min(2, z * 1.15))}
                style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#ffffff", display: "flex", alignItems: "center" }}
              >
                <ZoomIn size={16} />
              </button>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", minWidth: 36, textAlign: "right" }}>
                {Math.round(zoom * 100)}%
              </span>
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0, textAlign: "center" }}>
              Drag to reposition · Scroll or use slider to zoom
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setCropSrc(null)}
                disabled={avatarUploading}
                style={{
                  flex: 1, padding: "10px 0", borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent", color: "rgba(255,255,255,0.7)",
                  fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={avatarUploading}
                style={{
                  flex: 2, padding: "10px 0", borderRadius: 8, border: "none",
                  background: "linear-gradient(135deg, #6a1fbf, #a64dff)",
                  color: "#ffffff", fontSize: 13, fontWeight: 600,
                  cursor: avatarUploading ? "not-allowed" : "pointer",
                  opacity: avatarUploading ? 0.7 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {avatarUploading ? "Uploading..." : "Apply & Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle = (padding = 24, marginBottom = 0) => ({
  padding,
  borderRadius: 14,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  marginBottom,
});
